import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type Operator } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardBody } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import { relativeTime } from '@/lib/utils';

export function OperatorsPage() {
  const qc = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<{ operator: Operator }>('/api/auth/me'),
  });
  const operators = useQuery({
    queryKey: ['operators'],
    queryFn: () => api.get<{ operators: Operator[] }>('/api/operators'),
  });

  const [inviteOpen, setInviteOpen] = useState(false);

  const setActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/api/operators/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/operators/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['operators'] }),
  });

  const meId = meQuery.data?.operator.id;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Operators</h1>
          <p className="text-sm text-slate-500 mt-1">
            Everyone with sign-in access to the control plane. Magic link is always available;
            password is optional per operator.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="self-start sm:self-auto shrink-0">
          <Plus size={14} /> Invite operator
        </Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {operators.isLoading ? (
            <div className="p-5 text-sm text-slate-500">Loading…</div>
          ) : !operators.data?.operators.length ? (
            <div className="p-8 text-center text-sm text-slate-500">No operators yet.</div>
          ) : (
            <div className="overflow-x-auto">
            <table className="w-full min-w-[680px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                  <th className="px-5 py-3">Email</th>
                  <th className="px-3 py-3">Name</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Password</th>
                  <th className="px-3 py-3">Last login</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {operators.data.operators.map((o) => (
                  <tr key={o.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-900">
                      {o.email}
                      {o.id === meId && <span className="ml-2 text-xs text-slate-500">(you)</span>}
                    </td>
                    <td className="px-3 py-3 text-slate-700">{o.name ?? '—'}</td>
                    <td className="px-3 py-3">
                      <Badge tone={o.active ? 'success' : 'neutral'}>
                        {o.active ? 'active' : 'inactive'}
                      </Badge>
                    </td>
                    <td className="px-3 py-3">
                      {o.has_password ? (
                        <Badge tone="info">set</Badge>
                      ) : (
                        <span className="text-xs text-slate-400">magic link only</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500">
                      {o.last_login ? relativeTime(o.last_login) : '—'}
                    </td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      {o.id !== meId && (
                        <>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setActive.mutate({ id: o.id, active: !o.active })}
                            disabled={setActive.isPending}
                          >
                            {o.active ? 'Deactivate' : 'Reactivate'}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              if (confirm(`Permanently delete ${o.email}? Activity log entries will be reassigned to no operator.`)) {
                                remove.mutate(o.id);
                              }
                            }}
                            title="Delete (hard)"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </CardBody>
      </Card>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
    </div>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ email: '', name: '' });
  const [error, setError] = useState<string | null>(null);

  const invite = useMutation({
    mutationFn: () =>
      api.post('/api/operators', {
        email: form.email.trim().toLowerCase(),
        name: form.name || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['operators'] });
      onOpenChange(false);
      setForm({ email: '', name: '' });
      setError(null);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Invite operator"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={() => invite.mutate()} disabled={invite.isPending || !form.email}>
            {invite.isPending ? 'Inviting…' : 'Send invite'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {error && <div className="text-xs text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="newop@example.com"
          />
          <p className="text-xs text-slate-500 mt-1">
            They can sign in immediately by requesting a magic link at <code className="font-mono">/login</code>.
            No invitation email is sent yet — share the URL with them out-of-band.
          </p>
        </div>
        <div>
          <Label htmlFor="name">Name (optional)</Label>
          <Input
            id="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
      </div>
    </Dialog>
  );
}
